import { useState, useRef, useEffect } from "react";
import { Checkbox, Chip, Input } from "@heroui/react";
import { Building, Link, User } from "assets/icons";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { Resource, ResourceType } from "shared/types/resource";

export type ResourceSelectionProps = {
  addResourceConnection?: any;
  removeResourceConnection?: any;
  defaultResources?: Resource[];
};

const ResourceSelection = ({
  addResourceConnection,
  removeResourceConnection,
  defaultResources = [],
}: ResourceSelectionProps) => {
  const [resourceDropdownOpen, setResourceDropdownOpen] = useState(false);
  const [selectedResources, setSelectedResources] = useState<string[]>(
    defaultResources ? defaultResources.map((resource) => resource.name) : [],
  );
  const [resources, setResources] = useState<Resource[]>(
    defaultResources || [],
  );
  const [searchTerm, setSearchTerm] = useState("");
  const resourceDropdownRef = useRef<HTMLInputElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const session = useSession();

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/tasks/resources/all`,
          { method: "GET" },
          session.session,
        );
        const data = await response.json();
        if (data && data.length > 0) {
          setResources(data);
        }
      } catch (error) {
        console.error("There was an error fetching resources:", error);
        toast.error("Error fetching resources");
      }
    };

    fetchResources();
  }, []);

  const toggleResource = (resource: string) => {
    if (selectedResources.includes(resource)) {
      setSelectedResources((prev) => prev.filter((r) => r !== resource));
    } else {
      setSelectedResources((prev) => [...prev, resource]);
    }
  };

  const handleSelect = (resource: Resource) => {
    if (resource) {
      if (selectedResources.includes(resource.name)) {
        // Remove connection if the resource is already selected
        removeResourceConnection(resource.id);
      } else {
        // Add connection if the resource isn't already selected
        addResourceConnection(resource);
      }

      // Toggle selection state
      toggleResource(resource.name);
    }
  };

  useEffect(() => {
    if (resourceDropdownOpen) {
      searchInputRef.current?.focus();
    }
  }, [resourceDropdownOpen]);

  const handleClickOutside = (event: any) => {
    if (
      resourceDropdownRef.current &&
      !resourceDropdownRef.current.contains(event.target)
    ) {
      setResourceDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filtering resources based on search term
  const filteredResources = resources.filter((resource) =>
    resource.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const renderIcon = (type: ResourceType) => {
    if (type === "building") {
      return <Building />;
    } else if (type === "contact") {
      return <User />;
    }
    return null;
  };

  // Extract selected resources from the main resources list
  const selectedResourceObjects = selectedResources
    .map((name) => resources.find((resource) => resource.name === name))
    .filter((resource): resource is Resource => Boolean(resource)); // Use a type guard here

  // Exclude selected resources from the filtered list
  const nonSelectedFilteredResources = filteredResources.filter(
    (resource) => !selectedResources.includes(resource.name),
  );

  return (
    <div className="relative">
      <div
        className="flex cursor-pointer items-center gap-2"
        onClick={() => setResourceDropdownOpen(!resourceDropdownOpen)}
      >
        <Chip variant={selectedResources.length === 0 ? "light" : "solid"}>
          <div className="flex">
            <Link className="mr-2 h-[20px] w-[20px]" />
            {selectedResources.length === 0 && "Koppel met"}
            {selectedResources.length === 1 && selectedResources[0]}
            {selectedResources.length > 1 &&
              `${selectedResources.length} koppelingen`}
          </div>
        </Chip>
      </div>

      {resourceDropdownOpen && (
        <div
          ref={resourceDropdownRef}
          className="shadow-lg absolute left-0 z-10 mt-2 w-64 rounded-md bg-white p-4"
        >
          <Input
            isDisabled={!addResourceConnection || !removeResourceConnection}
            ref={searchInputRef}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={
              addResourceConnection && removeResourceConnection
                ? "Zoek gebouw, contact of andere..."
                : "Niet aanpasbaar in taakweergave"
            }
            size="sm"
            className="w-full"
          />

          {/* Render selected resources */}
          {selectedResourceObjects.map((resource) => (
            <div
              key={resource.name}
              className="mb-2 flex items-center justify-between"
            >
              {renderIcon(resource.type)}
              <p>{resource.name}</p>
              <Checkbox
                isSelected={true}
                onChange={() => handleSelect(resource)}
              />
            </div>
          ))}

          {/* Render filtered resources if there's a search term */}
          {searchTerm &&
            nonSelectedFilteredResources.map((resource) => (
              <div
                key={resource.name}
                className="mb-2 flex items-center justify-between"
              >
                {renderIcon(resource.type)}
                <p>{resource.name}</p>
                <Checkbox
                  isSelected={selectedResources.includes(resource.name)}
                  onChange={() => handleSelect(resource)}
                />
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default ResourceSelection;
